import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type PublishImage =
  { kind: 'existing'; id: string; sha1: string; url: string } | { kind: 'new'; id: string; file: File };

export type PublishToCommunityForm = {
  name: string;
  description: string;
  category: string;
  tags: string[];
  thumbnail: PublishImage | null;
  /** Tool-only square icon shown in Community + the in-file Tools list; unused for workflows. */
  icon: PublishImage | null;
  carouselImages: PublishImage[];
  supportEmail: string;
  agreedToTerms: boolean;
};

/**
 * Draft Community-listing forms, keyed per resource being published — a workflow id for a workflow,
 * a `weave_app_id` for a tool. A tool and its workflow are separate listings, so they must not share
 * an entry.
 */
interface PublishToCommunityFormState {
  forms: Record<string, Partial<PublishToCommunityForm> | undefined>;
  seededFormKeys: Record<string, boolean>;
  setForm: (
    formKey: string,
    update:
      Partial<PublishToCommunityForm> | ((current: Partial<PublishToCommunityForm>) => Partial<PublishToCommunityForm>),
  ) => void;
  markFormSeeded: (formKey: string) => void;
  resetForm: (formKey: string) => void;
}

export const usePublishToCommunityFormStore = create<PublishToCommunityFormState>()(
  devtools(
    (set) => ({
      forms: {},
      seededFormKeys: {},
      setForm: (formKey, update) => {
        set((state) => {
          const current = state.forms[formKey];
          const patch = typeof update === 'function' ? update(current ?? {}) : update;
          return {
            forms: { ...state.forms, [formKey]: { ...current, ...patch } },
          };
        });
      },
      markFormSeeded: (formKey) => {
        set((state) =>
          state.seededFormKeys[formKey] ? state : { seededFormKeys: { ...state.seededFormKeys, [formKey]: true } },
        );
      },
      resetForm: (formKey) => {
        set((state) => {
          const { [formKey]: _seeded, ...restSeeded } = state.seededFormKeys;
          if (!state.forms[formKey]) return { seededFormKeys: restSeeded };
          const { [formKey]: _removed, ...rest } = state.forms;
          return { forms: rest, seededFormKeys: restSeeded };
        });
      },
    }),
    {
      name: 'Publish To Community Form Store',
      enabled: import.meta.env.DEV,
    },
  ),
);
